import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Button, RadioGroup, FormControlLabel, Radio, Checkbox,
  DialogContent, DialogActions, LinearProgress, Box, Paper,
} from '@mui/material';
import {
  collection, addDoc, serverTimestamp,
} from 'firebase/firestore';
import db from '../../../firebase';
import { opportunityDetailActions } from '../../../redux/opportunity/opportunity/opportunityDetailState';
import Dialog from '../../../components/atoms/Dialog';
import {
  TextArea, FormErrorText, LoadingButton,
} from '../../../components/atoms/Base';
import DataGrid from '../../../components/templates/DataGrid';
import Validation from './validation';
import DataSelectList from '../../../components/atoms/DataSelectList';

const types = [
  { value: 1, label: '可' },
  { value: 2, label: '条件付可' },
  { value: 0, label: '否' },
];

const DEFAULT_ANSWER = {
  id: '',
  shopPropriety: 1,
  orderRejectCode: '',
  shopProprietyRemarks: '',
};

const DEFAULT_ERROR_MESSAGE = {
  shopPropriety: '',
  shopProprietyRemarks: '',
  orderRejectCode: '',
};

export default function CheckCompDialog(props) {
  const [answer, setAnswer] = React.useState(DEFAULT_ANSWER);
  const [errorMessages, setErrorMessages] = useState(DEFAULT_ERROR_MESSAGE);
  const loading = useSelector((state) => state.commonStore.isLoading);
  const [checked, setChecked] = useState(false);
  const dispatch = useDispatch();
  const {
    id, isOpen, onClose, data,
  } = props;

  useEffect(() => {
    if (isOpen) {
      setChecked(false);
      setAnswer(DEFAULT_ANSWER);
      setErrorMessages(DEFAULT_ERROR_MESSAGE);
    }
  }, [isOpen]);

  const onChange = (e) => {
    const { name, value } = e.target;

    const tmpValue = name === 'shopPropriety'
      ? Number(value)
      : value;

    setAnswer((prevAnswer) => {
      const nextAnswer = {
        ...prevAnswer,
        [name]: tmpValue,
      };

      setErrorMessages((prevErrors) => ({
        ...prevErrors,
        [name]: Validation.formValidate(
          name,
          tmpValue,
          nextAnswer.shopPropriety,
          nextAnswer.orderRejectCode,
        ),
      }));

      return nextAnswer;
    });
  };

  const beforeSaveCheck = (newRow) => {
    let tempMessages = errorMessages;
    let isError = false;

    Object.keys(newRow).forEach((key) => {
      const msg = Validation.formValidate(
        key,
        newRow[key],
        newRow.shopPropriety,
        newRow.orderRejectCode,
      );
      if (msg) {
        isError = true;
      }
      tempMessages = {
        ...tempMessages,
        [key]: msg,
      };
    });

    setErrorMessages(tempMessages);
    return isError;
  };

  const addMessageToFirestore = async (tmpAnswer) => {
    try {
      const dt = new Date();
      const proprietyLabel = types.find(
        (type) => type.value === tmpAnswer.shopPropriety,
      )?.label ?? '';

      const message = `
      ${data.companyNameOpp} ${data.customerNameOpp}
      ${data.shopName}から${proprietyLabel}の回答がありました。
      ${!data.shopProprietyRemarks ? `コメント: ${tmpAnswer.shopProprietyRemarks}` : ''}
    `.trim();
      const dateString = `${dt.getFullYear()}年${dt.getMonth() + 1}月${dt.getDate()}日 ${dt.getHours()}時${dt.getMinutes()}分`;
      await addDoc(collection(db, 'messagesToAdmin'), {
        targetId: `${data.orderId}`,
        message,
        read: false,
        date: serverTimestamp(),
        dateString,
        type: 'order',
        salesUserId: data.salesUserId,
      });
    } catch (_) {}
  };

  const onClickSave = () => {
    if (beforeSaveCheck(answer) || !id) {
      return;
    }
    const tmpAnswer = {
      ...answer,
      orderId: data.orderId,
      opportunityId: id,
    };
    dispatch(opportunityDetailActions.saveAnswer(tmpAnswer));
    addMessageToFirestore(tmpAnswer);
  };

  return (
    <Dialog fullWidth isOpen={isOpen} onClose={onClose} title="回答">
      {loading && (<LinearProgress color="secondary" />)}
      <DialogContent dividers>
        {data.customerRequest && (
          <Paper sx={{ padding: 1, marginBottom: 1 }}>
            <Box fontWeight="bold">お客様からのご要望・ご質問があります。確認の上、回答をお願いします。</Box>
            {data.customerRequest && data.customerRequest.split('\n').map((t, i) => <Box key={`${t} ${i + 1}`}>{t}</Box>)}
            <FormControlLabel
              control={(
                <Checkbox
                  onChange={(e) => setChecked(e.target.checked)}
                  checked={checked}
                />
                        )}
              label="確認しました"
            />
          </Paper>
        )}
        <DataGrid title="受注可否" isRequired isEdit>
          <RadioGroup row value={answer.shopPropriety} name="shopPropriety" onChange={onChange}>
            {types.map((e) => (
              <FormControlLabel
                key={e.value}
                value={e.value}
                control={<Radio />}
                label={e.label}
              />
            ))}
          </RadioGroup>
          <FormErrorText>{errorMessages.shopPropriety}</FormErrorText>
        </DataGrid>
        {answer.shopPropriety === 0 && (
          <DataGrid title="不可理由" isRequired isEdit>
            <DataSelectList
              name="orderRejectCode"
              target="codes"
              error={!!errorMessages.orderRejectCode}
              parameter={{ codeCategory: 'shop_order_reject' }}
              value={answer.orderRejectCode}
              onChange={onChange}
              disableClearable
            />
            <FormErrorText>{errorMessages.orderRejectCode}</FormErrorText>
          </DataGrid>
        )}

        <DataGrid title="Cqreeへのコメント" isRequired={answer.shopPropriety === 2 || (answer.shopPropriety === 0 && answer.orderRejectCode === 99)} isEdit>
          <TextArea
            name="shopProprietyRemarks"
            style={{ width: '100%' }}
            value={answer.shopProprietyRemarks}
            minRows={10}
            onChange={onChange}
          />
          <FormErrorText>{errorMessages.shopProprietyRemarks}</FormErrorText>
        </DataGrid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">キャンセル</Button>
        <LoadingButton
          onClick={onClickSave}
          disabled={data.customerRequest && !checked}
        >
          回答する
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
